import React, { useState } from "react";
import axios from "axios";
import { ShoppingBag, X, Minus, Plus, Trash2, Loader2, CreditCard, Wallet, CheckCircle2, Flame, Lock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useKitchenStatus } from "../hooks/useKitchenStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const cart = useCart();
  const kitchen = useKitchenStatus();
  const isOpen = kitchen.is_open !== false;
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [loading, setLoading] = useState(null); // 'card' | 'cash' | null
  const [error, setError] = useState(null);
  const [cashConfirmation, setCashConfirmation] = useState(null); // { order_number, amount_total_cents }

  const TABLE_OPTIONS = [
    ...Array.from({ length: 22 }, (_, i) => ({
      value: String(i + 1),
      label: `Table ${i + 1}`,
    })),
    { value: "Bar", label: "Bar" },
    { value: "Pool Room", label: "Pool Room" },
    { value: "Arcade Room", label: "Arcade Room" },
    { value: "Dart Room", label: "Dart Room" },
  ];

  const seatLabel = (v) => (/^\d+$/.test(v) ? `Table ${v}` : v);

  const validate = () => {
    if (!name.trim()) {
      setError("Please enter your name before placing the order.");
      return false;
    }
    if (!tableNumber) {
      setError("Please pick your table (or Bar) before placing the order.");
      return false;
    }
    return true;
  };

  const handleCardCheckout = async () => {
    if (cart.items.length === 0) return;
    if (!validate()) return;
    setLoading("card");
    setError(null);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        cart: cart.items.map((i) => ({
          item_id: i.id,
          quantity: i.quantity,
          dip: i.dip || undefined,
          addons: i.addons?.length ? i.addons.map((a) => a.id) : undefined,
        })),
        origin_url: window.location.origin,
        customer_name: name || undefined,
        customer_phone: phone || undefined,
        customer_notes: notes || undefined,
        table_number: tableNumber || undefined,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      setError(
        e?.response?.data?.detail || e.message || "Could not start checkout."
      );
      setLoading(null);
    }
  };

  const handleCashCheckout = async () => {
    if (cart.items.length === 0) return;
    if (!validate()) return;
    setLoading("cash");
    setError(null);
    try {
      const { data } = await axios.post(`${API}/checkout/cash`, {
        cart: cart.items.map((i) => ({
          item_id: i.id,
          quantity: i.quantity,
          dip: i.dip || undefined,
          addons: i.addons?.length ? i.addons.map((a) => a.id) : undefined,
        })),
        customer_name: name || undefined,
        customer_phone: phone || undefined,
        customer_notes: notes || undefined,
        table_number: tableNumber || undefined,
      });
      cart.clear();
      setName("");
      setPhone("");
      setNotes("");
      setTableNumber("");
      setCashConfirmation({ ...data, table_number: tableNumber });
    } catch (e) {
      setError(
        e?.response?.data?.detail || e.message || "Could not place order."
      );
    } finally {
      setLoading(null);
    }
  };

  const closeDrawer = () => {
    setCashConfirmation(null);
    setError(null);
    cart.close();
  };

  if (!cart.isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={cart.close}
        data-testid="cart-backdrop"
      />
      {/* Panel */}
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-[#0a0a0a] border-l border-white/10 flex flex-col">
        <header className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            {cashConfirmation ? (
              <Flame className="w-5 h-5 text-[#FF3B30]" />
            ) : (
              <ShoppingBag className="w-5 h-5 text-[#FF3B30]" />
            )}
            <h2 className="font-['Anton'] text-2xl tracking-widest uppercase text-white">
              {cashConfirmation ? "Order Placed" : "Your Order"}
            </h2>
          </div>
          <button
            onClick={closeDrawer}
            data-testid="cart-close"
            className="text-zinc-400 hover:text-white transition-colors"
            aria-label="Close cart"
          >
            <X className="w-6 h-6" />
          </button>
        </header>

        {cashConfirmation ? (
          <div
            data-testid="cash-confirmation"
            className="flex-1 overflow-y-auto px-6 py-10 flex flex-col items-center text-center"
          >
            <CheckCircle2 className="w-16 h-16 text-emerald-400" />
            <h3 className="font-['Anton'] text-4xl uppercase tracking-tight text-white mt-6 leading-none">
              Thanks for your order!
            </h3>
            <p className="font-['Outfit'] text-zinc-400 mt-3 max-w-xs">
              The kitchen's got it. Show this screen at the counter to pay in cash.
            </p>

            <div className="mt-8 inline-block bg-black/40 border border-[#FF3B30]/40 px-8 py-5">
              <div className="font-['Outfit'] text-xs uppercase tracking-[0.3em] text-zinc-500">
                Order Number
              </div>
              <div
                data-testid="cash-order-number"
                className="font-['Anton'] text-4xl tracking-widest text-white mt-1"
              >
                {cashConfirmation.order_number}
              </div>
              <div className="mt-3 font-['Anton'] text-2xl text-[#FF8C00]">
                ${(cashConfirmation.amount_total_cents / 100).toFixed(2)}
              </div>
              <div className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-1">
                Due at counter
              </div>
            </div>

            {cashConfirmation.table_number && (
              <div className="mt-5 font-['Outfit'] text-sm text-zinc-300">
                <span className="text-zinc-500 uppercase tracking-[0.25em] text-xs">Seated at</span>{" "}
                <span className="font-['Anton'] tracking-widest text-white text-lg">
                  {seatLabel(cashConfirmation.table_number)}
                </span>
              </div>
            )}

            <ul className="mt-8 w-full max-w-xs space-y-1 font-['Outfit'] text-sm text-zinc-400">
              {(cashConfirmation.cart_summary || []).map((line, idx) => (
                <li key={`${line.item_id}-${idx}`} className="flex justify-between">
                  <span>
                    {line.quantity}× {line.name}
                  </span>
                  <span className="text-zinc-300">
                    ${(line.line_total_cents / 100).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={closeDrawer}
              data-testid="cash-confirm-close"
              className="mt-10 inline-flex items-center font-['Anton'] tracking-widest uppercase bg-[#FF3B30] hover:bg-white hover:text-black text-white px-6 py-3 text-sm transition-colors"
            >
              Back to Menu
            </button>
          </div>
        ) : (
        <>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {cart.items.length === 0 ? (
            <div
              data-testid="cart-empty"
              className="h-full flex flex-col items-center justify-center text-center text-zinc-500"
            >
              <ShoppingBag className="w-12 h-12 mb-4 opacity-30" />
              <p className="font-['Outfit'] text-sm uppercase tracking-widest">
                Your order is empty
              </p>
              <p className="font-['Outfit'] text-xs text-zinc-600 mt-2">
                Add something from the menu to get started.
              </p>
            </div>
          ) : (
            <ul className="space-y-4">
              {cart.items.map((item) => (
                <li
                  key={item.id}
                  data-testid={`cart-line-${item.id}`}
                  className="flex gap-4 bg-[#111] border border-white/5 p-3"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-['Anton'] uppercase tracking-tight text-white text-lg leading-tight">
                        {item.name}
                      </h3>
                      <button
                        onClick={() => cart.remove(item.lineKey)}
                        data-testid={`cart-remove-${item.lineKey}`}
                        className="text-zinc-500 hover:text-[#FF3B30] transition-colors"
                        aria-label="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {(item.dipName || item.addons?.length > 0) && (
                      <p className="font-['Outfit'] text-xs text-zinc-400 mt-1">
                        {[
                          item.dipName ? `w/ ${item.dipName}` : null,
                          ...(item.addons || []).map((a) => `+ ${a.name.replace(/^Add /, "")}`),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                    <p className="font-['Outfit'] text-sm text-[#FF8C00] mt-1">
                      ${item.price.toFixed(2)} each
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center border border-white/10">
                        <button
                          onClick={() => cart.setQty(item.lineKey, item.quantity - 1)}
                          data-testid={`cart-dec-${item.lineKey}`}
                          className="w-8 h-8 flex items-center justify-center text-zinc-300 hover:bg-white/10"
                          aria-label="Decrease"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span
                          data-testid={`cart-qty-${item.lineKey}`}
                          className="w-8 h-8 flex items-center justify-center font-['Anton'] text-white"
                        >
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => cart.setQty(item.lineKey, item.quantity + 1)}
                          data-testid={`cart-inc-${item.lineKey}`}
                          className="w-8 h-8 flex items-center justify-center text-zinc-300 hover:bg-white/10"
                          aria-label="Increase"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="font-['Anton'] text-white tracking-wider">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.items.length > 0 && (
          <footer className="border-t border-white/10 px-6 py-5 space-y-4 bg-black">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Your name *"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="cart-name-input"
                className={`bg-[#111] border px-3 py-2 text-sm text-white font-['Outfit'] placeholder:text-zinc-600 focus:border-[#FF3B30] outline-none ${
                  error && !name.trim()
                    ? "border-[#FF3B30]"
                    : "border-white/10"
                }`}
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="cart-phone-input"
                className="bg-[#111] border border-white/10 px-3 py-2 text-sm text-white font-['Outfit'] placeholder:text-zinc-600 focus:border-[#FF3B30] outline-none"
              />
            </div>

            <textarea
              placeholder="Special requests? (allergies, no onions, extra sauce…)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="cart-notes-input"
              rows={2}
              maxLength={500}
              className="w-full bg-[#111] border border-white/10 px-3 py-2 text-sm text-white font-['Outfit'] placeholder:text-zinc-600 focus:border-[#FF3B30] outline-none resize-none"
            />

            <select
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              data-testid="cart-table-select"
              required
              className={`w-full bg-[#111] border px-3 py-2 text-sm font-['Outfit'] focus:border-[#FF3B30] outline-none appearance-none ${
                tableNumber ? "text-white" : "text-zinc-500"
              } ${
                error && !tableNumber
                  ? "border-[#FF3B30]"
                  : "border-white/10"
              }`}
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23A1A1AA' d='M6 8L0 0h12z'/></svg>\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                paddingRight: "32px",
              }}
            >
              <option value="" disabled>
                Where are you sitting? *
              </option>
              {TABLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="text-white bg-[#111]">
                  {opt.label}
                </option>
              ))}
            </select>

            <div className="flex items-center justify-between">
              <span className="font-['Outfit'] text-xs uppercase tracking-[0.3em] text-zinc-500">
                Subtotal
              </span>
              <span
                data-testid="cart-subtotal"
                className="font-['Anton'] text-3xl tracking-wider text-white"
              >
                ${cart.subtotal.toFixed(2)}
              </span>
            </div>

            {error && (
              <p
                data-testid="cart-error"
                className="font-['Outfit'] text-sm text-[#FF3B30]"
              >
                {error}
              </p>
            )}

            <div className="grid grid-cols-1 gap-2">
              {!isOpen && (
                <div
                  data-testid="cart-closed-notice"
                  className="bg-[#FF3B30]/10 border border-[#FF3B30] px-4 py-3 flex items-start gap-3"
                >
                  <Lock className="w-4 h-4 text-[#FF3B30] flex-shrink-0 mt-0.5" />
                  <div className="font-['Outfit'] text-sm text-zinc-200">
                    <span className="font-['Anton'] tracking-widest uppercase text-[#FF3B30] block text-xs mb-1">
                      Kitchen is closed
                    </span>
                    {kitchen.message || "We're done for the night. Try us again tomorrow."}
                  </div>
                </div>
              )}

              <button
                onClick={handleCardCheckout}
                disabled={loading !== null || !isOpen}
                data-testid="cart-checkout-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-white hover:text-black text-white font-['Anton'] tracking-widest uppercase py-4 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#FF3B30] disabled:hover:text-white"
              >
                {loading === "card" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Redirecting…
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    Pay with Card · ${cart.subtotal.toFixed(2)}
                  </>
                )}
              </button>

              <button
                onClick={handleCashCheckout}
                disabled={loading !== null || !isOpen}
                data-testid="cart-cash-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 hover:border-white hover:bg-white hover:text-black text-white font-['Anton'] tracking-widest uppercase py-4 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-white/30 disabled:hover:bg-transparent disabled:hover:text-white"
              >
                {loading === "cash" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Placing order…
                  </>
                ) : (
                  <>
                    <Wallet className="w-4 h-4" />
                    Pay in Cash
                  </>
                )}
              </button>
            </div>

            <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-zinc-600 text-center">
              Card · Stripe secured · Cash · Pay on pickup
            </p>
          </footer>
        )}
        </>
        )}
      </aside>
    </div>
  );
}
